/**
 * Display names for tags whose stored form is not what a reader wants.
 *
 * `dominance` is stored as tori / uke / neutral and mirrored into `tags`
 * as `#tori` / `#uke` / `#neutral`, which keeps the data in the source
 * vocabulary and lets anyone searching in judo terms find it.
 *
 * But that is judo vocabulary, and this is a BJJ map. Practitioners here
 * say "top" and "bottom", or "attacking" and "defending"; almost nobody
 * outside a judo dojo reads `#uke` and thinks "the person who needs to
 * escape". Rendering the raw term would make the single most useful
 * facet on the panel — is this a good place to be, or a bad one — the
 * least legible thing on it.
 *
 * So the tag keeps the source term and the label is plain English. The
 * translation lives here rather than in the data, because it is a
 * presentation decision and the data should not have to know about it.
 */
export const TAG_LABELS: Record<string, string> = {
  '#tori': 'Attacking',
  '#uke': 'Defending',
  '#neutral': 'Even',
};

/** Tags that answer "how is this going for me", rather than "what is it". */
export const ADVANTAGE_TAGS = new Set(Object.keys(TAG_LABELS));

export function tagLabel(tag: string): string {
  const key = tag.startsWith('#') ? tag : `#${tag}`;
  return TAG_LABELS[key] ?? (tag.startsWith('#') ? tag : `#${tag}`);
}

export function isAdvantageTag(tag: string): boolean {
  return ADVANTAGE_TAGS.has(tag.startsWith('#') ? tag : `#${tag}`);
}

/**
 * Advantage reads better as a state than as another hashtag, so it gets
 * its own colour: warm for winning, cool for losing, muted for even.
 * These are deliberately low-saturation — the graph already spends its
 * colour budget on zones, and a shouting tag would compete with it.
 */
export const ADVANTAGE_STYLE: Record<string, { fg: string; bg: string; border: string }> = {
  '#tori': { fg: 'rgba(255,196,140,0.92)', bg: 'rgba(255,150,70,0.10)', border: 'rgba(255,150,70,0.32)' },
  '#uke': { fg: 'rgba(150,200,255,0.92)', bg: 'rgba(90,160,255,0.10)', border: 'rgba(90,160,255,0.32)' },
  '#neutral': { fg: 'rgba(215,215,225,0.75)', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.18)' },
};

export function advantageStyle(tag: string) {
  return ADVANTAGE_STYLE[tag.startsWith('#') ? tag : `#${tag}`];
}

/**
 * Cluster label for the advantage axis, in plain English.
 *
 * The canvas labels and the cluster key both had their own pretty-name
 * tables, and neither knew tori or uke — so switching to the advantage
 * lens put the raw judo terms on screen, which is the opposite of what
 * that lens is for. It is the approachable axis; it should read
 * approachably.
 *
 * AXIS-AWARE ON PURPOSE.
 * `neutral` belongs to two different axes and means something different
 * in each. Under position type it is standing and clinch — genuinely
 * "Neutral". Under advantage it is a position where neither player is
 * ahead, which reads better as "Even" and matches the chip in the
 * drawer. A lookup keyed only on the id cannot tell those apart, which
 * is why this takes the axis.
 */
export function advantageClusterLabel(id: string, clusterBy: string): string | undefined {
  if (clusterBy !== 'advantage') return undefined;
  return TAG_LABELS['#' + id];
}
